/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { DropdownMenu, HelperText } from '@sonarsource/echoes-react';
import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { throwGlobalError } from '~adapters/helpers/error';
import { getReleaseRisksBlob, getSbomReportBlob } from '../../api/releases';

const SBOM_DOWNLOADABLE_REPORT_FORMATS = [
  {
    formatLabel: 'dependencies.list.export_as_sbom.format.xml',
    fileFormat: 'cdx.xml',
    mediaType: 'application/vnd.cyclonedx+xml',
    type: 'cyclonedx',
    typeLabel: 'dependencies.list.export_as_sbom.type.cyclonedx',
  },
  {
    formatLabel: 'json',
    fileFormat: 'cdx.json',
    mediaType: 'application/vnd.cyclonedx+json',
    type: 'cyclonedx',
    typeLabel: 'dependencies.list.export_as_sbom.type.cyclonedx',
  },
  {
    formatLabel: 'dependencies.list.export_as_sbom.format.xml',
    fileFormat: 'spdx23.xml',
    mediaType: 'application/spdx+xml',
    type: 'spdx_23',
    typeLabel: 'dependencies.list.export_as_sbom.type.spdx_23',
  },
  {
    formatLabel: 'json',
    fileFormat: 'spdx23.json',
    mediaType: 'application/spdx+json',
    type: 'spdx_23',
    typeLabel: 'dependencies.list.export_as_sbom.type.spdx_23',
  },
  {
    formatLabel: 'json',
    fileFormat: 'spdx30.json',
    mediaType: 'application/spdx+json',
    type: 'spdx_30',
    typeLabel: 'dependencies.list.export_as_sbom.type.spdx_30',
  },
];

const RELEASE_RISKS_DOWNLOADABLE_REPORT_FORMATS = [
  {
    formatLabel: 'json',
    fileFormat: 'release_risks.json',
    mediaType: 'application/json',
    type: 'release_risks',
    typeLabel: 'sca.risk.report.name',
  },
  {
    formatLabel: 'csv',
    fileFormat: 'release_risks.csv',
    mediaType: 'text/csv',
    type: 'release_risks',
    typeLabel: 'sca.risk.report.name',
  },
];

interface Props {
  branch?: string;
  component: string;
  componentName: string;
}

/**
 * Downloads are returned direct from the API. The type query parameter and
 * Accept header determines the type of report to be downloaded.
 * In order to support this, we need to use an XHR client that can set
 * headers, and then trigger the download by simulating the creation of
 * an <a href> element with a download attribute and whose href is a
 * Blob that contains the report.
 */
const downloadReport = ({
  reportFn,
  branch,
  component,
  filename,
  mediaType,
  type,
}: {
  branch: string | undefined;
  component: string;
  filename: string;
  mediaType: string;
  reportFn: (params: {
    branch: string | undefined;
    component: string;
    mediaType: string;
    type: string;
  }) => Promise<Blob>;
  type: string;
}) => {
  reportFn({
    branch,
    mediaType,
    component,
    type,
  })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const anchorElement = document.createElement('a');
      anchorElement.download = filename;
      anchorElement.href = url;
      anchorElement.target = '_blank';

      anchorElement.click();
      anchorElement.remove();
      URL.revokeObjectURL(url);
    })
    .catch(throwGlobalError);
};

function filenameFn(componentName: string, fileFormat: string, branch?: string) {
  const branchStr = branch ? `-${branch}` : '';
  return `${componentName}${branchStr}.${fileFormat}`;
}

function SbomReportsDropdownOptions(props: Readonly<Props>) {
  const { branch, componentName, component } = props;
  return SBOM_DOWNLOADABLE_REPORT_FORMATS.map(
    ({ formatLabel, fileFormat, mediaType, type, typeLabel }) => {
      const filename = filenameFn(componentName, fileFormat, branch);

      return (
        <DropdownMenu.ItemButton
          key={filename}
          onClick={() => {
            downloadReport({
              reportFn: getSbomReportBlob,
              branch,
              component,
              filename,
              mediaType,
              type,
            });
          }}
        >
          <FormattedMessage id={typeLabel} />
          <HelperText as="span" className="sw-ml-1">
            (<FormattedMessage id={formatLabel} />)
          </HelperText>
        </DropdownMenu.ItemButton>
      );
    },
  );
}

export const MemoizedSbomReportsDropdownOptions = memo(SbomReportsDropdownOptions);

function ReleaseRisksReportsDropdownOptions(props: Readonly<Props>) {
  const { branch, componentName, component } = props;
  return RELEASE_RISKS_DOWNLOADABLE_REPORT_FORMATS.map(
    ({ formatLabel, fileFormat, mediaType, type, typeLabel }) => {
      const filename = filenameFn(componentName, fileFormat, branch);

      return (
        <DropdownMenu.ItemButton
          key={filename}
          onClick={() => {
            downloadReport({
              reportFn: getReleaseRisksBlob,
              branch,
              component,
              filename,
              mediaType,
              type,
            });
          }}
        >
          <FormattedMessage id={typeLabel} />
          <HelperText as="span" className="sw-ml-1">
            (<FormattedMessage id={formatLabel} />)
          </HelperText>
        </DropdownMenu.ItemButton>
      );
    },
  );
}

export const MemoizedReleaseRisksReportsDropdownOptions = memo(ReleaseRisksReportsDropdownOptions);

export function ScaReportOverviewOptions(props: Readonly<Props>) {
  return (
    <>
      <DropdownMenu.GroupLabel>
        <FormattedMessage id="sca.sbom.report" />
      </DropdownMenu.GroupLabel>
      <MemoizedSbomReportsDropdownOptions {...props} />
      <DropdownMenu.Separator />
      <DropdownMenu.GroupLabel>
        <FormattedMessage id="sca.risk.report" />
      </DropdownMenu.GroupLabel>
      <MemoizedReleaseRisksReportsDropdownOptions {...props} />
    </>
  );
}
